import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;
  /** @type {string} */
  #productHandle = '';

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    return /** @type {HTMLAnchorElement} */ (this.closest('product-card')?.querySelector('a[ref="productCardLink"]'))
      ?.href;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (!this.#cachedProductHtml) {
      await this.fetchProductPage(this.productPageUrl);
    }

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      );

      await this.updateQuickAddModal(freshHtmlCopy);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    if (isMobileBreakpoint()) {
      // Mobile: open bottom sheet instead of dialog
      this.#stayVisibleUntilDialogCloses(dialogComponent);
      dialogComponent.openBottomSheet();
    } else {
      // Desktop: use regular dialog modal
      this.#stayVisibleUntilDialogCloses(dialogComponent);
      dialogComponent.showDialog();
    }
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
    dialogComponent.closeBottomSheet();
  };

  /**
   * Fetches the product quick-view content using the lightweight quick-view template.
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const quickViewUrl = new URL(productPageUrl, window.location.origin);
      const templateSuffix = window.globalJsVariables?.template_suffix || '';
      if (templateSuffix === 'loyalty') {
        quickViewUrl.searchParams.set('view', 'quick-view-redeem');
      } else {
        quickViewUrl.searchParams.set('view', 'quick-view');
      }

      const response = await fetch(quickViewUrl.toString(), {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      this.#cachedProductHtml = html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * Uses the same innerHTML + theme:section:morphed pattern as
   * handleBottomSheetLearnMore so that Alpine re-initialises cleanly
   * and subscription widget / add-to-cart events work.
   * @param {Document} newHtml - The new HTML.
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]');

    if (!productGrid) return;

    // Extract and store product handle for later use
    this.#productHandle = this.productPageUrl?.match(/\/products\/([^/?#]+)/)?.[1] || '';

    if (isMobileBreakpoint()) {
      // Mobile: inject the whole product grid (outerHTML) so the
      // .product-details Alpine x-data context, subscription widget,
      // and add-to-cart button events are all preserved — same approach
      // as handleBottomSheetLearnMore.
      const bottomSheetContent = document.getElementById('quick-add-bottom-sheet-content');
      if (!bottomSheetContent) return;

      // 1. Product info row: image (left) + title & PV (right)
      let productInfoHtml = '<div class="bs-product-info">';
      const productImageSrc = this.dataset.productImage;
      if (productImageSrc) {
        productInfoHtml += `<div class="bs-product-image"><img src="${productImageSrc}" alt="${this.dataset.productTitle || ''}" width="76" height="76"></div>`;
      }
      productInfoHtml += '<div class="bs-product-meta">';
      productInfoHtml += `<a href="${this.productPageUrl}" class="bs-product-title">${this.dataset.productTitle || ''}</a>`;
      const pvInfo = productGrid.querySelector('.sku-pv-wrapper span:nth-of-type(2)');
      if (pvInfo) {
        const pvValueSpan = pvInfo.querySelector('.fw-600');
        if (pvValueSpan) {
          productInfoHtml += `<div class="bs-pv-value">PV: ${pvValueSpan.textContent.trim()}</div>`;
        }
      }
      productInfoHtml += '</div></div>';

      // Add <legend>Size </legend> inside each fieldset.variant-option
      const variantFieldset = productGrid.querySelector('variant-picker .variant-option');
      if (variantFieldset) {
        variantFieldset.insertAdjacentHTML('afterbegin', '<legend class="bs-variant-picker-legend fs-500">Size </legend>');
      }


      // 2. Learn more button — label lives on <quick-add-dialog>, not this card component
      const learnMoreLabel =
        document.getElementById('quick-add-dialog')?.getAttribute('data-learn-more-label') || 'Learn more';
      const learnMoreHtml = `<div class="bs-learn-more-wrapper"><learn-more-button class="button button-white bs-learn-more-btn w-100" data-product-handle="${this.#productHandle}">${learnMoreLabel}</learn-more-button></div>`;
      const addToCartComponent = productGrid.querySelector('add-to-cart-component');
      if (addToCartComponent) {
        if (!addToCartComponent.querySelector('.bs-learn-more-wrapper')) {
          addToCartComponent.insertAdjacentHTML('afterbegin', learnMoreHtml);
        }
      }

      // Hide .subscription-widget, .manage-box-button, and .add-to-cart-button for collection template 'loyalty', otherwise show them
      const templateSuffix = window.globalJsVariables?.template_suffix || '';
      const widgets = [
        productGrid.querySelector('.subscription-widget'),
        productGrid.querySelector('.manage-box-button'),
        productGrid.querySelector('.add-to-cart-button'),
        productGrid.querySelector('.redeem-btn')
      ];

      widgets.forEach(widget => {
        if (!widget) return;
        // Only show .redeem-button on 'loyalty', always hide otherwise
        if (widget.classList.contains('redeem-btn')) {
          widget.classList[templateSuffix === 'loyalty' ? 'remove' : 'add']('hidden');
        } else {
          widget.classList[templateSuffix === 'loyalty' ? 'add' : 'remove']('hidden');
        }
      });

      // 3. Loyalty points (from the product card on the collection page)
      let loyaltyHtml = '';
      const loyaltySpans = productGrid.querySelector('.loyalty-points');
      loyaltyHtml = `<div class="bs-loyalty-points text-center" style="display: none;">${loyaltySpans?.innerHTML || ''} <span class="fw-400 fs-14">Loyalty Points</span></div>`;

      bottomSheetContent.innerHTML =
        productInfoHtml +
        productGrid.outerHTML +
        loyaltyHtml;
    } else {
      // Desktop: morph the product grid into the modal content
      const modalContent = document.getElementById('quick-add-modal-content');
      if (!modalContent) return;

      morph(modalContent, productGrid);
    }

    // Re-initialize Alpine (and other components) on the injected content.
    // Uses the same pattern as handleBottomSheetLearnMore: wait for DOM to
    // settle, then dispatch theme:section:morphed so main.ts runs
    // Alpine.destroyTree + Alpine.initTree and re-inits Tabs, Carousels, etc.
    const injectedContainer = isMobileBreakpoint()
      ? document.getElementById('quick-add-bottom-sheet-content')
      : document.getElementById('quick-add-modal-content');

    if (injectedContainer) {
      setTimeout(() => {
        try {
          document.dispatchEvent(
            new CustomEvent('theme:section:morphed', {
              detail: { morphedElement: injectedContainer, sectionId: 'quick-add-content' },
            })
          );

          requestAnimationFrame(() => {
            injectedContainer.querySelectorAll('.variant-option__button-label').forEach(
              /** @param {HTMLElement} label */ (label) => {
                label.style.display = 'none';
                void label.offsetHeight;
                label.style.display = '';
              }
            );
          });
        } catch (_e) {
          // Component initialization is best-effort
        }
      }, 100);
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {Element | null} */
  #bottomSheet = null;
  /** @type {Element | null} */
  #bottomSheetOverlay = null;
  /** @type {Element | null} */
  #bottomSheetCloseBtn = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    // Setup bottom sheet close handlers
    this.#bottomSheet = this.querySelector('[data-quick-add-bottom-sheet]');
    this.#bottomSheetOverlay = this.querySelector('[data-quick-add-sheet-overlay]');
    this.#bottomSheetCloseBtn = this.querySelector('[data-quick-add-sheet-close]');

    if (this.#bottomSheetOverlay) {
      this.#bottomSheetOverlay.addEventListener('click', () => this.closeBottomSheet());
    }
    if (this.#bottomSheetCloseBtn) {
      this.#bottomSheetCloseBtn.addEventListener('click', () => this.closeBottomSheet());
    }

    // Close bottom sheet on ESC key
    document.addEventListener('keydown', this.#handleBottomSheetKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    document.removeEventListener('keydown', this.#handleBottomSheetKeyDown);
  }

  /**
   * Opens the bottom sheet modal (mobile)
   */
  openBottomSheet() {
    if (!this.#bottomSheet) return;
    this.#bottomSheet.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  /**
   * Closes the bottom sheet modal (mobile)
   */
  closeBottomSheet() {
    if (!this.#bottomSheet) return;
    this.#bottomSheet.classList.remove('is-open');
    document.body.style.overflow = '';
    this.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Handles ESC key to close bottom sheet
   */
  #handleBottomSheetKeyDown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key !== 'Escape') return;
    if (this.#bottomSheet?.classList.contains('is-open')) {
      event.preventDefault();
      this.closeBottomSheet();
    }
  };

  /**
   * Closes the dialog (and bottom sheet)
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
    this.closeBottomSheet();
  };

  /**
   * Handles "Learn more" button click from the bottom sheet.
   * Closes the bottom sheet and opens the shared mini-PDP modal
   * (productDetailModal) so Collection Learn More matches Loyalty / Kit / BP.
   * @param {string} productHandle - The product handle (e.g. "my-product")
   */
  handleBottomSheetLearnMore(productHandle) {
    if (!productHandle) return;

    this.closeBottomSheet();

    const store = /** @type {{ openModal?: (input: { handle: string }) => void } | undefined } */ (
      window.Alpine?.store?.('productDetailModal')
    );

    if (typeof store?.openModal === 'function') {
      store.openModal({ handle: productHandle });
      return;
    }

    // Fallback if the Alpine module has not loaded yet (should be rare on collection pages).
    console.warn('[QuickAdd] productDetailModal store unavailable; falling back to legacy learn-more modal');
    const modalId = document.getElementById('quickAddLearnMoreModal')
      ? 'quickAddLearnMoreModal'
      : 'quizProductQuickView';
    const contentSelector = modalId === 'quickAddLearnMoreModal'
      ? `#${modalId} [data-quick-add-learn-more-content]`
      : `#${modalId} [data-quiz-product-quick-view-content]`;

    const quickViewContent = document.querySelector(contentSelector);
    if (quickViewContent) {
      quickViewContent.innerHTML = `
        <div class="quick-view-loader">
          <div class="quick-view-loader-spinner"></div>
          <span>Loading...</span>
        </div>
      `;
    }

    const openModalFn = /** @type {Function | undefined} */ (/** @type {any} */ (window).openModal);
    if (typeof openModalFn === 'function') {
      openModalFn(modalId);
    }

    fetch(`/products/${productHandle}`)
      .then((response) => response.text())
      .then((html) => {
        const newHtml = new DOMParser().parseFromString(html, 'text/html');
        const content = newHtml?.body?.querySelector('.product-information__grid')?.outerHTML || '';

        if (quickViewContent) {
          quickViewContent.innerHTML = content;

          setTimeout(() => {
            try {
              document.dispatchEvent(
                new CustomEvent('theme:section:morphed', {
                  detail: { morphedElement: quickViewContent, sectionId: 'quick-view-content' },
                })
              );
            } catch (_e) {
              // Component initialization is best-effort
            }
          }, 100);
        }
      })
      .catch((err) => {
        console.error('Failed to load product quick view:', err);
      });
  }

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.bs-product-title'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

class LearnMoreButton extends HTMLElement {
  connectedCallback() {
    this.setAttribute('role', 'button');
    this.setAttribute('tabindex', '0');
    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeydown);
    }, 1000);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
  }

  #handleClick = (/** @type {Event} */ e) => {
   e.preventDefault();
   e.stopPropagation();
   e.stopImmediatePropagation();
    const productHandle = this.dataset.productHandle;
    if (!productHandle) return;

    const dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent instanceof QuickAddDialog) {
      dialogComponent.handleBottomSheetLearnMore(productHandle);
    }
  };

  #handleKeydown = (/** @type {KeyboardEvent} */ e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.#handleClick(e);
    }
  };
}

if (!customElements.get('learn-more-button')) {
  customElements.define('learn-more-button', LearnMoreButton);
}